import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { BillingDiagnosticGroups, missingRevenueText } from '../../components/BillingDiagnosticGroups';
import { useDepartment } from '../../contexts/DepartmentContext';
import {
  Users, UserCog, Crown, FileText, CalendarDays,
  Shield, Bell, BellRing, Megaphone, CalendarCheck, ClipboardEdit,
  ImagePlus, DollarSign, AlertTriangle, PhoneCall, PartyPopper,
  TrendingUp, ArrowUpRight, Award, Activity, ClipboardCheck
} from 'lucide-react';
import CrmFollowUpsCard from '../../components/CrmFollowUpsCard';
import OfficeCommandCenter from '../../components/dashboard/OfficeCommandCenter';
import RecentPreviewsCard from '../../components/admin/RecentPreviewsCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const KpiCard = ({ icon: Icon, label, value, sub, link, tone = 'default', testid }) => {
  const tones = {
    default: { border: 'border-[#262626]', iconBg: 'bg-[#D4AF37]/12', iconColor: 'text-[#D4AF37]', valueColor: 'text-white' },
    alert: { border: 'border-red-700/60', iconBg: 'bg-red-500/15', iconColor: 'text-red-400', valueColor: 'text-red-400' },
    warning: { border: 'border-[#D4AF37]/70', iconBg: 'bg-[#D4AF37]/20', iconColor: 'text-[#D4AF37]', valueColor: 'text-[#D4AF37]' },
    success: { border: 'border-green-700/50', iconBg: 'bg-green-500/15', iconColor: 'text-green-400', valueColor: 'text-green-400' },
  };
  const t = tones[tone] || tones.default;
  const content = (
    <div data-testid={testid} className={`bg-[#141414] border ${t.border} rounded-lg p-5 hover:bg-[#181818] hover:border-[#3f3f3f] transition-all h-full kpi-card`}>
      <div className="flex items-start justify-between mb-3">
        <div className={`w-11 h-11 ${t.iconBg} rounded-lg flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${t.iconColor}`} />
        </div>
        {link && <ArrowUpRight className="w-4 h-4 text-[#525252]" />}
      </div>
      <p className="text-[#A3A3A3] text-[11px] uppercase tracking-[0.14em] font-semibold mb-1.5">{label}</p>
      <p className={`text-[34px] leading-none font-extrabold tracking-tight ${t.valueColor}`}>{value}</p>
      {sub && <p className="text-[#737373] text-xs mt-2 font-medium">{sub}</p>}
    </div>
  );
  return link ? <Link to={link} className="block">{content}</Link> : content;
};

// iter319 — ONE Command Center for every non-field role; content is permission/scope driven
// server-side. The old "Today's Pulse" CompanyDashboard below is retired (no longer rendered).
const Dashboard = () => <OfficeCommandCenter />;

// eslint-disable-next-line no-unused-vars
const CompanyDashboard = () => {
  const [k, setK] = useState(null);
  const [legacyStats, setLegacyStats] = useState({});
  const [attention, setAttention] = useState(null);
  const navigate = useNavigate();
  const { activeDepartment } = useDepartment();

  const load = useCallback(() => {
    const dept = activeDepartment && activeDepartment !== 'all' ? `?trade_id=${activeDepartment}` : '';
    Promise.all([
      axios.get(`${API}/admin/dashboard-kpis${dept}`, { withCredentials: true }),
      axios.get(`${API}/admin/stats`, { withCredentials: true }).catch(() => ({ data: {} })),
      axios.get(`${API}/admin/invoices/attention`, { withCredentials: true }).catch(() => ({ data: null })),
    ]).then(([a, b, c]) => { setK(a.data); setLegacyStats(b.data); setAttention(c.data); }).catch(console.error);
  }, [activeDepartment]);

  useEffect(() => { load(); }, [load]);

  if (!k) return <div className="text-[#A3A3A3]">Loading dashboard...</div>;

  const overview = k.overview || {};

  return (
    <div data-testid="admin-dashboard-page" className="space-y-8">
      <div>
        <p className="text-[#D4AF37] text-xs font-bold tracking-[0.3em] uppercase mb-2">
          Command Center
          {k.department && k.department !== 'all' && (
            <span data-testid="dept-scope-badge" className="ml-3 text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 rounded bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/30">
              {k.department.replace(/_/g, ' ')} only
            </span>
          )}
        </p>
        <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-white mb-1">Today's <span className="text-[#D4AF37]">Pulse</span></h2>
        <p className="text-[#A3A3A3]">{new Date(k.today).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</p>
      </div>

      {/* Quick stats row */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <KpiCard icon={Users} label="Customers" value={overview.total_customers ?? 0} link="/admin/customers" testid="kpi-customers" />
        <KpiCard icon={UserCog} label="Staff" value={overview.total_employees ?? 0} link="/admin/employees" testid="kpi-employees" />
        <KpiCard icon={Activity} label="Clocked In" value={overview.techs_clocked_in ?? 0} sub="Active right now" link="/admin/timesheets" testid="kpi-clocked-in" tone={overview.techs_clocked_in > 0 ? 'success' : 'default'} />
        <KpiCard icon={Crown} label="Active Members" value={overview.active_memberships ?? 0} link="/admin/memberships" testid="kpi-members" />
        <KpiCard icon={FileText} label="Open Invoices" value={overview.pending_invoices ?? 0} link="/admin/invoices?status=pending" testid="kpi-invoices" />
        <KpiCard icon={CalendarDays} label="Today's Jobs" value={overview.todays_jobs ?? 0} link="/admin/schedule" testid="kpi-jobs" />
      </div>

      {/* CRM Follow-Ups (above Needs Your Attention, only renders if any are due) */}
      <CrmFollowUpsCard />
      <RecentPreviewsCard />

      {/* Action-required row */}
      <div>
        <p className="text-[#D4AF37] text-xs font-bold tracking-[0.2em] uppercase mb-3">Needs Your Attention</p>
        {/* iter310 — Same shared Billing Diagnostics calculation as the Invoices banner. */}
        {attention && (attention.total_needing_action ?? attention.count) > 0 && (
          <div
            role="button"
            tabIndex={0}
            onClick={() => navigate('/admin/invoices?attention=1')}
            data-testid="dashboard-unsent-alert"
            className={`block mb-4 rounded-lg border p-4 transition-colors cursor-pointer ${attention.at_risk_count > 0 ? 'border-red-700/60 bg-red-500/10 hover:bg-red-500/15' : 'border-[#D4AF37]/60 bg-[#D4AF37]/10 hover:bg-[#D4AF37]/15'}`}
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className={`w-5 h-5 mt-0.5 ${attention.at_risk_count > 0 ? 'text-red-400' : 'text-[#D4AF37]'}`} />
              <div className="flex-1 min-w-0 space-y-2">
                <p className={`font-bold ${attention.at_risk_count > 0 ? 'text-red-300' : 'text-[#D4AF37]'}`}>
                  {(attention.total_needing_action ?? attention.count)} billing item{(attention.total_needing_action ?? attention.count) === 1 ? '' : 's'} need attention
                  {attention.at_risk_count > 0 && <> — <span className="text-white" data-testid="dashboard-missing-revenue">{missingRevenueText(attention.missing_revenue)} potential missing revenue</span></>}
                </p>
                <BillingDiagnosticGroups groups={attention.groups} onSelect={(g) => navigate(`/admin/invoices?attention=1&group=${g}`)} testidPrefix="dashboard-group" />
                {attention.followups?.open > 0 && (
                  <p className="text-[11px] text-[#A3A3A3]" data-testid="dashboard-followups">
                    {attention.followups.open} open billing follow-up(s){attention.followups.overdue ? <> · <span className="text-red-300 font-bold">{attention.followups.overdue} overdue</span></> : ''}{attention.followups.due_this_week ? ` · ${attention.followups.due_this_week} due this week` : ''}
                    {' · '}<Link to="/admin/billing-diagnostics?tab=followup" onClick={(e) => e.stopPropagation()} className="text-[#D4AF37] hover:underline" data-testid="dashboard-followup-link">Weekly Follow-Up</Link>
                  </p>
                )}
              </div>
              <ArrowUpRight className="w-4 h-4 text-[#525252]" />
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            icon={BellRing}
            label="Unread Notifications"
            value={k.notifications?.unread ?? 0}
            tone={k.notifications?.unread > 0 ? 'warning' : 'default'}
            link="/profile"
            testid="kpi-notifications"
          />
          <KpiCard
            icon={Megaphone}
            label="Pending Acknowledgments"
            value={k.pending_acknowledgments ?? 0}
            sub="Announcements you haven't acked"
            link="/admin/announcements"
            tone={k.pending_acknowledgments > 0 ? 'warning' : 'default'}
            testid="kpi-pending-acks"
          />
          <KpiCard
            icon={CalendarCheck}
            label="Time-Off Requests"
            value={k.pending_timeoff ?? 0}
            sub="Awaiting your approval"
            link="/admin/timeoff"
            tone={k.pending_timeoff > 0 ? 'warning' : 'default'}
            testid="kpi-timeoff"
          />
          <KpiCard
            icon={ClipboardEdit}
            label="Time-Card Edits"
            value={k.pending_timecard_edits ?? 0}
            sub="Pending review"
            link="/admin/timesheets"
            tone={k.pending_timecard_edits > 0 ? 'warning' : 'default'}
            testid="kpi-tc-edits"
          />
          <KpiCard
            icon={ClipboardCheck}
            label="Job Approvals"
            value={k.pending_job_approvals ?? 0}
            sub="Tech completions awaiting review"
            link="/admin/job-approvals"
            tone={k.pending_job_approvals > 0 ? 'warning' : 'default'}
            testid="kpi-job-approvals"
          />
          <KpiCard
            icon={Award}
            label="Bonus Requests"
            value={k.pending_bonus_requests ?? 0}
            sub="Awaiting approval"
            link="/admin/bonus-requests"
            tone={k.pending_bonus_requests > 0 ? 'warning' : 'default'}
            testid="kpi-bonus-requests"
          />
          <KpiCard
            icon={ImagePlus}
            label="Photo Approvals"
            value={k.pending_photo_approvals ?? 0}
            sub="Tech uploads to review (6B)"
            link="/admin/approvals"
            tone={k.pending_photo_approvals > 0 ? 'warning' : 'default'}
            testid="kpi-photos"
          />
          <KpiCard
            icon={DollarSign}
            label="VIP Payouts Owed"
            value={`$${(k.pending_vip_payouts?.amount ?? 0).toFixed(2)}`}
            sub={`${k.pending_vip_payouts?.count ?? 0} bonus(es) approved`}
            link="/admin/vips"
            tone={k.pending_vip_payouts?.count > 0 ? 'warning' : 'default'}
            testid="kpi-vip-payouts"
          />
          <KpiCard
            icon={AlertTriangle}
            label="Low Inventory Alerts"
            value={k.low_inventory_count ?? k.low_inventory?.length ?? 0}
            sub={k.low_inventory?.length > 0 ? `e.g. ${k.low_inventory[0].item_name}${k.low_inventory[0].location_name ? ` @ ${k.low_inventory[0].location_name}${k.low_inventory[0].estimated ? ' (est.)' : ''}` : ''}` : 'Stock healthy'}
            link="/admin/inventory-hub/stock?low=1"
            tone={k.low_inventory?.length > 0 ? 'alert' : 'success'}
            testid="kpi-low-inventory"
          />
          <KpiCard
            icon={PartyPopper}
            label="Upcoming Holidays"
            value={k.upcoming_holidays?.length ?? 0}
            sub={k.upcoming_holidays?.length > 0 ? k.upcoming_holidays[0].name || k.upcoming_holidays[0].date : 'None in next 30 days'}
            link="/admin/system"
            testid="kpi-holidays"
          />
        </div>
      </div>

      {/* On-call + Audit recent */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Link to="/admin/oncall" data-testid="oncall-card" className="bg-[#141414] border border-[#262626] rounded-md p-5 hover:border-[#3f3f3f] transition-colors block">
          <div className="flex items-center gap-2 mb-3">
            <PhoneCall className="w-4 h-4 text-[#D4AF37]" />
            <p className="text-[#A3A3A3] text-xs uppercase tracking-wider">On Call</p>
          </div>
          {k.upcoming_oncall ? (
            <>
              <p className="text-white text-lg font-bold">{k.upcoming_oncall.employee_name || k.upcoming_oncall.user_name}</p>
              <p className="text-[#A3A3A3] text-xs mt-1">{k.upcoming_oncall.start_date} → {k.upcoming_oncall.end_date}</p>
            </>
          ) : (
            <p className="text-[#525252] text-sm">No on-call scheduled <span className="block text-xs mt-1">(coming in Phase 6B)</span></p>
          )}
        </Link>

        <div data-testid="audit-card" className="bg-[#141414] border border-[#262626] rounded-md p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-[#D4AF37]" />
              <p className="text-[#A3A3A3] text-xs uppercase tracking-wider">Audit Activity</p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-[#D4AF37] text-xs font-bold flex items-center gap-1"><TrendingUp className="w-3 h-3" /> {k.audit_log?.count_7d ?? 0} events in 7d</span>
              <Link to="/admin/audit-log" className="text-[#A3A3A3] hover:text-white text-xs">View all →</Link>
            </div>
          </div>
          <div className="space-y-2">
            {(k.audit_log?.recent || []).length === 0 ? (
              <p className="text-[#525252] text-sm text-center py-4">No recent activity</p>
            ) : k.audit_log.recent.slice(0, 5).map((e) => (
              <div key={e.id} className="flex items-center justify-between text-sm border-b border-[#262626] last:border-0 pb-2 last:pb-0">
                <div>
                  <span className="text-white">{e.actor_name}</span>
                  <span className="text-[#525252] mx-2">→</span>
                  <span className="text-[#D4AF37] font-mono text-xs">{e.action}</span>
                </div>
                <span className="text-[#525252] text-xs">{new Date(e.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Low-inventory detailed list (if any) */}
      {k.low_inventory?.length > 0 && (
        <div data-testid="low-inv-card" className="bg-[#1a0e0e] border border-red-700/40 rounded-md p-5">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-red-400" />
            <p className="text-red-400 text-xs uppercase tracking-wider font-bold">Restock Soon</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {k.low_inventory.map((i) => (
              <Link key={`${i.id}-${i.location_name || ''}`} to="/admin/inventory-hub/stock?low=1" data-testid={`low-inv-item-${i.id}`} className="bg-[#0A0A0A] border border-[#262626] rounded p-3 hover:border-[#3f3f3f] transition-colors block">
                <p className="text-white text-sm font-medium">{i.item_name}</p>
                <p className="text-[#A3A3A3] text-xs">{i.truck_id} • <span className="text-red-400 font-bold">{i.quantity}</span>/{i.min_quantity} {i.unit}</p>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
